import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView as useCountInView } from 'react-intersection-observer';

// Grouped achievements by category (Sorted in descending order by year)
const championships = [
  { year: '2016', title: 'Indian Rally Champion', category: 'Group A · Foreign Bike', medal: 'Champion', icon: 'flag' },
  { year: '2016', title: 'Rajasthan Desert Gold', category: 'Up to 250cc Category', medal: 'Gold', icon: 'trophy' },
  { year: '2015', title: 'National Gold Champion', category: 'Up to 400cc Racing', medal: 'Gold', icon: 'trophy' },
  { year: '2014', title: 'KTM Orange Day', category: 'KTM 390 Duke · First Ever', medal: 'Gold', icon: 'flag' },
  { year: '2004', title: 'Dirt Track Champion', category: 'Indian National', medal: 'Champion', icon: 'trophy' },
  { year: '2003', title: 'Dirt Track Champion', category: 'Indian National', medal: 'Gold', icon: 'trophy' },
];

const podiums = [
  { year: '2019', title: 'National Sprint Race', position: '1ST', detail: 'Gold Medal', color: 'red' },
  { year: '2018', title: 'Yamaha 250cc', position: '2ND', detail: 'vs. Factory Teams', color: 'slate' },
  { year: '2017', title: 'Himalaya Rally', position: '3RD', detail: 'Overall Finish', color: 'crimson' },
];

const enduranceFeats = [
  {
    title: 'Desert Storm 2000km',
    detail: 'Completed the full rally with damaged rear suspension',
    stat: '2000',
    unit: 'KM',
  },
  {
    title: 'Desert Marathon Solo',
    detail: 'Only rider to complete the 200km stage with both wheels punctured',
    stat: '200',
    unit: 'KM',
  },
  {
    title: 'Bangalore to Goa',
    detail: 'Covered the full inter-state route in record time',
    stat: '<4',
    unit: 'HRS',
  },
  {
    title: 'TSD National Race',
    detail: 'Beat major teams in a stock Hyundai Santro',
    stat: '01',
    unit: 'CAR',
  },
];

const teams = [
  'TVS Racing',
  'CEAT',
  'Motul',
  'Metro Tyres',
  'Dream Machines',
  'Angata Racing',
];

const stats = [
  { end: 9, label: 'Championships', suffix: '+' },
  { end: 6, label: 'Gold Medals', suffix: '' },
  { end: 14, label: 'Major Wins', suffix: '' },
  { end: 20, label: 'Years Racing', suffix: '+' },
];

const tabs = [
  { id: 'CHAMPIONS', label: 'Championships', count: championships.length },
  { id: 'PODIUMS', label: 'Podiums', count: podiums.length },
  { id: 'FEATS', label: 'Endurance', count: enduranceFeats.length },
  { id: 'TEAMS', label: 'Teams', count: teams.length },
];

const Achievements = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [countRef, countInView] = useCountInView({ triggerOnce: true, threshold: 0.2 });
  const [activeTab, setActiveTab] = useState('CHAMPIONS');

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.08, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  const TrophyIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0011 15.9V18H8v2h8v-2h-3v-2.1c2.12-.39 3.79-2.03 4.39-4.34C19.08 11.23 21 9.15 21 6.6V5c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
    </svg>
  );

  const FlagIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
    </svg>
  );

  return (
    <section
      id="achievements"
      className="relative py-20 md:py-28 overflow-hidden bg-[#0A0A0A] text-white select-none"
    >
      {/* Red Ambient Background Glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(225,6,0,0.18),rgba(0,0,0,0))]" />
        <div className="absolute top-1/3 -right-40 w-96 h-96 bg-[#E10600]/15 rounded-full blur-[128px]" />
        <div className="absolute bottom-10 -left-40 w-96 h-96 bg-red-600/15 rounded-full blur-[128px]" />
        <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Checkered Flag Corner Pattern */}
      <div className="absolute top-8 right-8 grid grid-cols-6 gap-[2px] opacity-15 pointer-events-none">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 ${
              (Math.floor(i / 6) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
            }`}
          />
        ))}
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <motion.h2
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase font-black leading-none"
            >
              CAREER{' '}
              <span className="text-transparent bg-clip-text bg-red-700">
                LEGACY
              </span>
            </motion.h2>
          </div>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="text-sm md:text-base text-zinc-400 max-w-md leading-relaxed border-l-2 border-[#E10600]/60 pl-4"
          >
            Two decades on the throttle — forged through dirt, desert, tarmac,
            and every terrain in between.
          </motion.p>
        </div>

        {/* Stats Dashboard */}
        <motion.div
          ref={countRef}
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12 md:mb-16"
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="relative group p-4 md:p-6 rounded-2xl bg-zinc-900/50 backdrop-blur-xl border border-white/10 hover:border-[#E10600]/50 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-2 right-3 opacity-20 group-hover:opacity-40 transition-opacity">
                <span className="font-mono text-[9px] tracking-widest text-white">
                  0{i + 1}
                </span>
              </div>
              <div className="font-heading text-3xl md:text-5xl font-black text-white group-hover:text-[#E10600] transition-colors">
                {countInView ? (
                  <CountUp end={stat.end} duration={2.5} suffix={stat.suffix} />
                ) : (
                  <span>0{stat.suffix}</span>
                )}
              </div>
              <span className="text-[10px] md:text-xs tracking-[0.2em] text-zinc-400 uppercase font-mono mt-1 block">
                {stat.label}
              </span>
              <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-[#E10600] to-rose-500 group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          variants={fadeUp}
          custom={4}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-8 md:mb-10"
        >
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 px-4 md:px-6 py-2.5 md:py-3 rounded-full font-mono text-[11px] md:text-xs tracking-widest uppercase transition-all duration-300 flex items-center gap-2.5 border ${
                  activeTab === tab.id
                    ? 'bg-[#E10600] text-white border-[#E10600] shadow-lg shadow-[#E10600]/30'
                    : 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10 hover:text-white hover:border-white/20'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] ${
                    activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-white/10 text-white/60'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          {/* CHAMPIONSHIPS */}
          {activeTab === 'CHAMPIONS' && (
            <motion.div
              key="champions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4"
            >
              {championships.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group relative p-5 md:p-6 rounded-2xl bg-zinc-900/60 border border-white/10 hover:border-[#E10600]/40 transition-all duration-300 backdrop-blur-md overflow-hidden"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="font-mono text-2xl md:text-3xl font-black text-[#E10600]">
                      {item.year}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full text-[9px] md:text-[10px] font-mono tracking-widest uppercase ${
                        item.medal === 'Gold'
                          ? 'bg-gradient-to-br from-red-500 to-rose-700 text-white border border-red-400/30'
                          : 'bg-gradient-to-br from-[#E10600] to-red-800 text-white'
                      }`}
                    >
                      {item.medal}
                    </span>
                  </div>

                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-[#E10600]/10 group-hover:bg-[#E10600]/20 flex items-center justify-center mb-4 transition-colors">
                    {item.icon === 'trophy' ? (
                      <TrophyIcon className="w-5 h-5 md:w-6 md:h-6 text-[#E10600]" />
                    ) : (
                      <FlagIcon className="w-5 h-5 md:w-6 md:h-6 text-[#E10600]" />
                    )}
                  </div>

                  <h4 className="font-heading text-base md:text-lg text-white tracking-wide leading-tight mb-1.5">
                    {item.title}
                  </h4>

                  <p className="text-xs md:text-sm text-zinc-400 leading-relaxed">
                    {item.category}
                  </p>

                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#E10600] to-rose-500 group-hover:w-full transition-all duration-500" />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* PODIUMS */}
          {activeTab === 'PODIUMS' && (
            <motion.div
              key="podiums"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4"
            >
              {podiums.map((item, i) => {
                const colorMap = {
                  red: 'from-red-400 to-[#E10600] text-white',
                  slate: 'from-slate-200 to-slate-400 text-slate-900',
                  crimson: 'from-rose-500 to-red-800 text-white',
                };
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="group relative p-6 md:p-8 rounded-2xl bg-zinc-900/60 border border-white/10 hover:border-[#E10600]/40 transition-all duration-300 overflow-hidden"
                  >
                    <div
                      className={`inline-block font-heading text-5xl md:text-6xl font-black bg-gradient-to-br ${colorMap[item.color]} bg-clip-text text-transparent mb-4`}
                    >
                      {item.position}
                    </div>

                    <div className="border-t border-white/10 pt-4 space-y-1.5">
                      <span className="font-mono text-xs text-[#E10600] tracking-widest">
                        {item.year}
                      </span>
                      <h4 className="font-heading text-lg md:text-xl text-white tracking-wide">
                        {item.title}
                      </h4>
                      <p className="text-xs md:text-sm text-zinc-400">
                        {item.detail}
                      </p>
                    </div>

                    <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#E10600] to-rose-500 group-hover:w-full transition-all duration-500" />
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* ENDURANCE FEATS */}
          {activeTab === 'FEATS' && (
            <motion.div
              key="feats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4"
            >
              {enduranceFeats.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="group relative p-5 md:p-6 rounded-2xl bg-zinc-900/60 border border-white/10 hover:border-[#E10600]/40 transition-all duration-300 overflow-hidden"
                >
                  <div className="flex items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0 text-center pr-4 md:pr-6 border-r border-white/10">
                      <div className="font-heading text-3xl md:text-5xl font-black bg-gradient-to-br from-[#E10600] to-rose-500 bg-clip-text text-transparent leading-none">
                        {item.stat}
                      </div>
                      <span className="text-[10px] md:text-xs tracking-[0.3em] text-zinc-500 uppercase font-mono mt-1 block">
                        {item.unit}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#E10600]/10 border border-[#E10600]/20 mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E10600]" />
                        <span className="text-[9px] md:text-[10px] tracking-widest uppercase text-[#E10600] font-mono">
                          Feat
                        </span>
                      </div>
                      <h4 className="font-heading text-base md:text-lg text-white tracking-wide leading-tight mb-2">
                        {item.title}
                      </h4>
                      <p className="text-xs md:text-sm text-zinc-400 leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#E10600] to-rose-500 group-hover:w-full transition-all duration-500" />
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* TEAMS */}
          {activeTab === 'TEAMS' && (
            <motion.div
              key="teams"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="relative p-6 md:p-10 rounded-2xl bg-zinc-900/60 border border-white/10 backdrop-blur-md"
            >
              <div className="text-center mb-8 md:mb-10">
                <span className="inline-block text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#E10600] font-mono mb-3">
                  · Represented ·
                </span>
                <h3 className="font-heading text-2xl md:text-4xl text-white tracking-wide">
                  National Team Sponsors
                </h3>
                <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
                  Backed by India's most respected motorsport brands across a
                  20-year career.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                {teams.map((team, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="group relative p-4 md:p-6 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#E10600]/40 hover:bg-white/[0.06] transition-all duration-300 text-center"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#E10600]/10 group-hover:bg-[#E10600]/20 flex items-center justify-center mx-auto mb-3 transition-colors">
                      <span className="font-heading text-lg md:text-xl text-[#E10600] font-black">
                        {team.charAt(0)}
                      </span>
                    </div>
                    <span className="font-heading text-sm md:text-base text-white tracking-wide block">
                      {team}
                    </span>
                    <span className="text-[9px] md:text-[10px] text-zinc-500 tracking-widest uppercase font-mono mt-1 block">
                      Partner
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Track Tag Line */}
        <motion.div
          variants={fadeUp}
          custom={6}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mt-16 md:mt-20 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center gap-3 md:gap-4 text-[10px] md:text-xs font-mono tracking-[0.3em] text-zinc-500 uppercase"
        >
          <span>DIRT</span>
          <span className="text-[#E10600]">•</span>
          <span>DESERT</span>
          <span className="text-[#E10600]">•</span>
          <span>TARMAC</span>
          <span className="text-[#E10600]">•</span>
          <span>TRACK</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;